import React, { useState, useMemo, useEffect } from 'react';
import { Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Button } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import {
    PositionModel,
    PositionType,
    Side,
    OptionType,
    OptionStyle,
    BudgetModel,
    GetCustomerDataResponse,
    GetCustomerSettingsResponse,
    LocationModel,
} from '../../../../../../Generated/Commodity-Management-Api';
import { StoneXDatePicker } from '../../../../../StoneXMui';
import { StoneXGrid, StoneXLoading, StoneXRow } from '../../../../../StoneX';
import { useLoadingState } from '../../../../../../Hooks';
import { parseCustomData, getToolDescription } from './Types';

const MMBTU_PER_CONTRACT = 10000;

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

interface HedgingPositionReportRow {
    monthKey: string;
    month: string;
    contracts: number;
    counterpartyName: string;
    settlement: string;           // L / P / E
    tool: string;
    purchasePrice: number;
    marketPrice: number;
    decile: number | string;
    strike: number;
    priceTime: number | string;
    coverage: number;             // fraction (0-1)
    cumulativeCoverage: number;   // fraction (0-1)
    tradeDate: string;
    maxContracts: number;
    position: PositionModel;
}

interface HedgingPositionReportProps {
    customerData?: GetCustomerDataResponse;
    customerSettings?: GetCustomerSettingsResponse;
    defaultQuoteDate?: Date;
}

/**
 * Determine the settlement indicator: L (Last Day Settle), P (Penultimate), or E (European).
 * Based on the legacy Piedmont report logic:
 *   - Counterparty "BP" on/after 1/1/2010 => L, otherwise P
 *   - Appends " E" when customData.optionSettlement == 2 or position.optionStyle === European
 */
const getSettlementIndicator = (position: PositionModel): string => {
    const customData = parseCustomData(position.customData);
    let lp = 'P';
    const counterparty = (position.counterparty?.counterpartyName || '').toUpperCase();
    const tradeDate = position.tradeDate ? new Date(position.tradeDate) : null;
    if (counterparty === 'BP' && tradeDate && tradeDate >= new Date('2010-01-01')) {
        lp = 'L';
    }
    const isEuropean =
        Number(customData.optionSettlement) === 2 ||
        position.optionStyle === OptionStyle.European;
    if (isEuropean) {
        lp += ' E';
    }
    return lp;
};

const HedgingPositionReport: React.FC<HedgingPositionReportProps> = ({
    customerData,
    defaultQuoteDate,
}) => {
    const loadingState = useLoadingState();
    const [positions, setPositions] = useState<PositionModel[]>([]);
    const [budgets, setBudgets] = useState<BudgetModel[]>([]);
    const [quoteDate, setQuoteDate] = useState<Date>(defaultQuoteDate || new Date());
    const [hasRun, setHasRun] = useState<boolean>(false);

    // Default start date = first of next month, end date = one year from today
    const [startMonth, setStartMonth] = useState<number>(dayjs().add(1, 'month').month() + 1);
    const [startYear, setStartYear] = useState<number>(dayjs().add(1, 'month').year());
    const [endMonth, setEndMonth] = useState<number>(dayjs().add(1, 'year').month() + 1);
    const [endYear, setEndYear] = useState<number>(dayjs().add(1, 'year').year());

    const locations: LocationModel[] = customerData?.locations || [];
    const [selectedLocation, setSelectedLocation] = useState<number | undefined>(undefined);

    // Default to first location once customer data loads
    useEffect(() => {
        if (selectedLocation === undefined && locations.length > 0 && locations[0].locationId !== undefined) {
            setSelectedLocation(locations[0].locationId);
        }
    }, [locations, selectedLocation]);

    const selectedLocationName = locations.find(l => l.locationId === selectedLocation)?.locationName || '';

    const fetchData = async () => {
        // Positions
        const posResponse = await CommodityManagementApi.listPositions(
            loadingState.setLoadingState,
            dayjs(quoteDate).format('YYYY-MM-DD'),
            true,  // includeExpirationDates
            true,  // includeMarketPrices
            true,  // useDisplayUnits
            undefined, // scenarioMarketPrices
            undefined, // commodityFilter
            selectedLocation ? [selectedLocation] : undefined,
            undefined, // counterpartyFilter
            undefined, // tradeStartDate
            undefined, // tradeEndDate
            startYear,
            startMonth,
            undefined, // budgetLabel
            endYear,
            endMonth,
            undefined, // budgetMonthEndFilterLabel
            undefined, // contractMonthStartFilterYear
            undefined, // contractMonthStartFilterMonth
            undefined, // contractMonthStartFilterLabel
            undefined, // contractMonthEndFilterYear
            undefined, // contractMonthEndFilterMonth
            undefined, // contractMonthEndFilterLabel
            undefined, // positionTypesFilter
            undefined, // dealTicketNumberFilter
            undefined, // dealTicketIdFilter
            undefined, // sideFilter
            undefined, // validatedFilter
            undefined, // currencyFilter
            undefined, // currency
            undefined, // expiryStatus
            undefined, // includeAllocations
            undefined, // includeAllocationOffset
            true       // filterByTradeDate - report runs as of the quote date
        );

        // Budgets (for coverage calculations)
        const budResponse = await CommodityManagementApi.listBudgets(
            null,
            dayjs(quoteDate).format('YYYY-MM-DD'),
            startYear,
            startMonth,
            undefined, // budgetStartMonthLabel
            endYear,
            endMonth,
            undefined, // budgetEndMonthLabel
            undefined, // fiscalYearsFilter
            undefined, // commoditiesFilter
            selectedLocation ? [selectedLocation] : undefined,
            undefined, // counterpartiesFilter
            undefined, // budgetRoundsFilter
            undefined, // includeExpirationDates
            false,     // includePositions
            false,     // includeMarketPrices
            false      // includeBudgetRounds
        );

        setPositions(posResponse.data.rows || []);
        setBudgets(budResponse.data.rows || []);
        setHasRun(true);
    };

    const handleSubmit = () => {
        fetchData();
    };

    // Build report rows with coverage computed against budget volumes per month
    const reportRows: HedgingPositionReportRow[] = useMemo(() => {
        if (positions.length === 0) return [];

        // Budget volume per month (MMBtus)
        const budgetVolumeByMonth = new Map<string, number>();
        budgets.forEach(b => {
            if (b.budgetDate?.year !== undefined && b.budgetDate?.month !== undefined) {
                const key = `${b.budgetDate.year}-${String(b.budgetDate.month).padStart(2, '0')}`;
                budgetVolumeByMonth.set(key, (budgetVolumeByMonth.get(key) ?? 0) + (b.volume ?? 0));
            }
        });

        // Build unsorted rows
        const rows: HedgingPositionReportRow[] = positions.map(position => {
            const customData = parseCustomData(position.customData);
            const budgetMonth = position.budgetDate.month ?? 1;
            const budgetYear = position.budgetDate.year ?? 2000;
            const monthKey = `${budgetYear}-${String(budgetMonth).padStart(2, '0')}`;
            const volume = position.volume ?? 0;
            const contracts = volume / MMBTU_PER_CONTRACT;
            const sideMultiplier = position.side === Side.Buy ? 1 : -1;
            const budgetVolume = budgetVolumeByMonth.get(monthKey) ?? 0;
            const coverage = budgetVolume > 0 ? (volume * sideMultiplier) / budgetVolume : 0;
            const maxContracts = budgetVolume / MMBTU_PER_CONTRACT;

            return {
                monthKey,
                month: dayjs(`${budgetYear}-${budgetMonth}-01`).format('MMM-YYYY'),
                contracts,
                counterpartyName: position.counterparty?.counterpartyName || '',
                settlement: getSettlementIndicator(position),
                tool: getToolDescription(position),
                purchasePrice: position.price ?? 0,
                marketPrice: position.markToMarketPrice ?? 0,
                decile: customData.decile ?? '',
                strike: position.optionStrike ?? 0,
                priceTime: typeof customData.trigger === 'boolean'
                    ? String(customData.trigger)
                    : (customData.trigger ?? ''),
                coverage,
                cumulativeCoverage: 0, // filled in after sort
                tradeDate: position.tradeDate ? dayjs(position.tradeDate).format('MM/DD/YYYY') : '',
                maxContracts,
                position,
            };
        });

        // Sort by month then trade date
        rows.sort((a, b) => {
            const dateCompare = a.monthKey.localeCompare(b.monthKey);
            if (dateCompare !== 0) return dateCompare;
            return a.tradeDate.localeCompare(b.tradeDate);
        });

        // Cumulative coverage is the total coverage through the current month,
        // reset when the month changes.
        let monthCumulative = 0;
        let lastMonthKey = '';
        rows.forEach(r => {
            if (r.monthKey !== lastMonthKey) {
                monthCumulative = 0;
                lastMonthKey = r.monthKey;
            }
            monthCumulative += r.coverage;
            r.cumulativeCoverage = monthCumulative;
        });

        return rows;
    }, [positions, budgets]);

    const formatCurrency4 = (v: number | null | undefined) =>
        v != null ? v.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 4, maximumFractionDigits: 4 }) : '';
    const formatCurrency3 = (v: number | null | undefined) =>
        v ? v.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '';
    const formatPercent = (v: number | null | undefined) =>
        v != null ? (v * 100).toLocaleString(undefined, { maximumFractionDigits: 0 }) + '%' : '';
    const formatContracts = (v: number | null | undefined) =>
        v != null ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : '';

    const columnDefs: ColDef<HedgingPositionReportRow>[] = useMemo(() => [
        { field: 'month', headerName: 'Month', width: 110, pinned: 'left' },
        { field: 'contracts', headerName: 'Contracts', width: 100, type: 'numericColumn', valueFormatter: p => formatContracts(p.value) },
        { field: 'counterpartyName', headerName: 'Counterparty', width: 130 },
        { field: 'settlement', headerName: 'Last Day Settle (L) / Penultimate (P) / European (E)', width: 260 },
        { field: 'tool', headerName: 'Tool', width: 110 },
        { field: 'purchasePrice', headerName: 'Purchase Price', width: 130, type: 'numericColumn', valueFormatter: p => formatCurrency4(p.value) },
        { field: 'marketPrice', headerName: 'Price (GDI)', width: 120, type: 'numericColumn', valueFormatter: p => formatCurrency4(p.value) },
        { field: 'decile', headerName: 'Decile', width: 80 },
        { field: 'strike', headerName: 'Strike / Ceiling / Floor', width: 160, type: 'numericColumn', valueFormatter: p => formatCurrency3(p.value) },
        { field: 'priceTime', headerName: 'Price / Time', width: 110 },
        { field: 'coverage', headerName: '% Coverage', width: 110, type: 'numericColumn', valueFormatter: p => formatPercent(p.value) },
        { field: 'cumulativeCoverage', headerName: 'Cumulative Coverage', width: 150, type: 'numericColumn', valueFormatter: p => formatPercent(p.value) },
        { field: 'tradeDate', headerName: 'Trade Date', width: 110 },
        { field: 'maxContracts', headerName: 'Max # Contracts', width: 130, type: 'numericColumn', valueFormatter: p => formatContracts(p.value) },
    ], []);

    const handleDownloadExcel = () => {
        if (reportRows.length === 0) return;

        const HEADERS = [
            'Month',
            'Contracts',
            'Counterparty',
            'Last Day Settle (L) / Penultimate (P) / European (E)',
            'Tool',
            'Purchase Price',
            'Price (GDI)',
            'Decile',
            'Strike / Ceiling / Floor',
            'Price / Time',
            '% Coverage',
            'Cumulative Coverage',
            'Trade Date',
            'Max # Contracts',
        ];

        const title = `${selectedLocationName} Hedging Position Report`.trim();
        const subtitle = `Report as of ${dayjs(quoteDate).format('M/D/YYYY')} Close`;

        const aoa: (string | number)[][] = [];
        aoa.push([title]);
        aoa.push([subtitle]);
        aoa.push(HEADERS);

        reportRows.forEach(r => {
            aoa.push([
                r.month,
                r.contracts,
                r.counterpartyName,
                r.settlement,
                r.tool,
                r.purchasePrice,
                r.marketPrice,
                typeof r.decile === 'number' ? r.decile : (r.decile || ''),
                r.strike,
                typeof r.priceTime === 'number' ? r.priceTime : (r.priceTime || ''),
                r.coverage,
                r.cumulativeCoverage,
                r.tradeDate,
                r.maxContracts,
            ]);
        });

        const sheet = XLSX.utils.aoa_to_sheet(aoa);
        const lastCol = HEADERS.length - 1;
        sheet['!merges'] = [
            { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
            { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } },
        ];

        const currency4 = '"$"#,##0.0000;[Red]("$"#,##0.0000)';
        const currency3 = '"$"#,##0.000;[Red]("$"#,##0.000)';
        const intFmt = '#,##0';
        const percentFmt = '0%';

        const dataStart = 3;
        for (let r = dataStart; r < aoa.length; r++) {
            const apply = (c: number, fmt: string) => {
                const ref = XLSX.utils.encode_cell({ r, c });
                const cell = sheet[ref];
                if (cell && typeof cell.v === 'number') {
                    cell.t = 'n';
                    cell.z = fmt;
                }
            };
            apply(1, intFmt);       // Contracts
            apply(5, currency4);    // Purchase Price
            apply(6, currency4);    // Price (GDI)
            apply(8, currency3);    // Strike
            apply(10, percentFmt);  // % Coverage
            apply(11, percentFmt);  // Cumulative Coverage
            apply(13, intFmt);      // Max # Contracts
        }

        sheet['!cols'] = [
            { wch: 12 }, { wch: 10 }, { wch: 16 }, { wch: 10 }, { wch: 12 },
            { wch: 14 }, { wch: 14 }, { wch: 8 }, { wch: 18 }, { wch: 12 },
            { wch: 12 }, { wch: 18 }, { wch: 12 }, { wch: 14 },
        ];

        const workbook = XLSX.utils.book_new();
        const sheetName = (selectedLocationName || 'Position').replace(/[\\/*?:[\]]/g, '').substring(0, 31) || 'Position';
        XLSX.utils.book_append_sheet(workbook, sheet, sheetName);

        const filename = `HedgingPositionReport_${dayjs(quoteDate).format('YYYYMMDD')}.xlsx`;
        XLSX.writeFile(workbook, filename);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>Hedging Position Report</Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
                <StoneXRow>
                    <StoneXDatePicker
                        label="Marking to Market Date"
                        value={quoteDate}
                        onChange={(date) => setQuoteDate(date || new Date())}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Start Month</InputLabel>
                        <Select
                            value={startMonth}
                            label="Start Month"
                            onChange={(e) => setStartMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Start Year"
                        type="number"
                        size="small"
                        value={startYear}
                        onChange={(e) => setStartYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>End Month</InputLabel>
                        <Select
                            value={endMonth}
                            label="End Month"
                            onChange={(e) => setEndMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="End Year"
                        type="number"
                        size="small"
                        value={endYear}
                        onChange={(e) => setEndYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Location</InputLabel>
                        <Select
                            value={selectedLocation ?? ''}
                            label="Location"
                            onChange={(e) => setSelectedLocation(Number(e.target.value))}
                        >
                            {locations.map((l: LocationModel) => (
                                <MenuItem key={l.locationId} value={l.locationId}>{l.locationName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </StoneXRow>
                <StoneXRow>
                    <Button variant="contained" onClick={handleSubmit}>Submit</Button>
                    <Button
                        variant="outlined"
                        onClick={handleDownloadExcel}
                        disabled={!hasRun || reportRows.length === 0}
                        sx={{ ml: 1 }}
                    >
                        Save to Excel
                    </Button>
                </StoneXRow>
            </Paper>

            {loadingState.isLoading() ? (
                <StoneXLoading show />
            ) : hasRun ? (
                <Paper sx={{ mb: 3 }}>
                    <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
                        <Typography variant="h6">{selectedLocationName} Hedging Position Report</Typography>
                        <Typography variant="body2">Report as of {dayjs(quoteDate).format('MM/DD/YYYY')} Close</Typography>
                    </Box>
                    <StoneXGrid
                        columns={columnDefs}
                        rows={reportRows}
                        hideGridOptions={true}
                    />
                </Paper>
            ) : null}
        </Box>
    );
};

export default HedgingPositionReport;
